import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import { useCan } from '@/domains/auth';
import { SAVE_REFUSED, saveFile } from '@/domains/api';
import type { IListRow } from '../../api/lists-contracts';
import type { IMetricsRowsRequest } from '../../api/metrics-updates-contracts';
import { metricsUpdatesApi } from '../../api/metrics-updates-api';
import { countOf } from '../../lists';
import { refusalMessage } from '../../refusal';

/**
 * "Download for mail merge…" (SR-315, WF-10 step 2 — the templated requests, until SR-335's
 * outreach sends them from the platform): survey-api's CSV of the ticked rows, else of the whole
 * list under the page's filters, one advisor a row with the figures the request asks about; and,
 * ticked by default, a request recorded for each — what the mail merge is about to send. Recording
 * takes `internal_fields.write`; without it the file alone is offered.
 */
export function MailMergeButton({
  survey,
  request,
  ticked,
  total,
  onDone
}: {
  survey: string;
  /** The rows read's body as the page sends it — the editions, tab and filters. */
  request: Omit<IMetricsRowsRequest, 'page' | 'pageSize'>;
  ticked: readonly IListRow[];
  total: number;
  onDone: (sentence: string) => void;
}) {
  const can = useCan();
  const mayRecord = can('internal_fields.write');
  const [open, setOpen] = React.useState(false);
  const [record, setRecord] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  const count = ticked.length > 0 ? ticked.length : total;

  const download = async () => {
    setBusy(true);
    setFailed(null);
    try {
      const recording = record && mayRecord;
      const { file, fileName } = await metricsUpdatesApi.mailMerge(survey, {
        ...request,
        record: recording,
        ...(ticked.length > 0
          ? { participants: ticked.map((row) => ({ participantId: row.participantId, survey: row.survey })) }
          : {})
      });
      const saved = saveFile(file, fileName ?? `metrics-requests-${survey}.csv`);
      setOpen(false);
      onDone(
        [
          saved ? `The mail merge file for ${countOf(count, 'advisor')} is downloaded.` : SAVE_REFUSED,
          recording && `A request is recorded for each.`
        ]
          .filter(Boolean)
          .join(' ')
      );
    } catch (error) {
      setFailed(refusalMessage(error, 'The file could not be made.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button type="button" size="sm" variant="outline" disabled={count === 0} onClick={() => setOpen(true)}>
        Download for mail merge…
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download for mail merge</DialogTitle>
            <DialogDescription>
              A CSV of {ticked.length > 0 ? `the ${countOf(count, 'selected advisor')}` : `every advisor on this list (${count.toLocaleString('en-US')})`}:
              name, email, firm, the editions they are on, their current figures and the date the
              figures are asked as of — for the “Updated metrics request” template.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={record && mayRecord}
                disabled={!mayRecord}
                onChange={(event) => setRecord(event.target.checked)}
              />
              Record a request for each advisor in the file
            </label>
            {!mayRecord && (
              <p className="text-sm text-muted-foreground">
                Your Team Portal roles do not include recording requests: the file alone is downloaded.
              </p>
            )}
            <p className="text-[0.8125rem] text-muted-foreground" data-mail-merge-how>
              Open the file in Word’s mail merge with the template and send from Outlook. Replies need
              nothing uploaded: an advisor who updates the survey leaves the list by themselves; figures
              sent by email go in with “Enter figures…”, “no change” with “Confirm unchanged…”. A firm’s own file goes
              through Import.
            </p>
            {failed && (
              <p role="alert" className="text-sm text-destructive">
                {failed}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={busy} onClick={() => void download()}>
              {busy ? 'Making the file…' : 'Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
